/**
 * The message composer shared by the chat tabs: a text input (Enter sends,
 * 4,000-char cap) and a Send button, disabled — with the reason shown —
 * until the socket is up and the server has confirmed this room's join.
 *
 * Tabs add their own controls through `leading` (e.g. the Support tab's
 * image button) and their own status lines through `notices`, rendered in
 * the same slot and style as the connection notice.
 */
import { useState, type KeyboardEvent, type ReactNode } from "react";
import { useChat } from "@/hooks/useChatSocket";
import { useAppSelector } from "@/state/hooks";
import styles from "./ChatRoom.module.css";

export const MAX_MESSAGE_CHARS = 4000;

/** Whether a message can go out on `roomId` right now, and if not, why. */
export function useRoomSendState(roomId: string | null): {
  canSend: boolean;
  notice: string | null;
} {
  const socketConnected = useAppSelector((s) => s.chat.socketConnected);
  const joinConfirmed = useAppSelector((s) =>
    roomId ? s.chat.joinedRoomIds.includes(roomId) : false,
  );
  if (!socketConnected) {
    return {
      canSend: false,
      notice: "Reconnecting to chat — you can't send messages until it's back.",
    };
  }
  // A send before the server's `joined` is refused ("Join the room first").
  if (!joinConfirmed) return { canSend: false, notice: "Joining the room…" };
  return { canSend: true, notice: null };
}

export interface ComposerNotice {
  text: string;
  tone: "info" | "warn" | "error";
  action?: { label: string; onClick: () => void };
  onDismiss?: () => void;
}

export function ChatComposer({
  roomId,
  inputId,
  placeholder,
  leading,
  notices = [],
}: {
  roomId: string;
  inputId: string;
  placeholder: string;
  leading?: ReactNode;
  notices?: ComposerNotice[];
}) {
  const { sendMessage } = useChat();
  const { canSend, notice } = useRoomSendState(roomId);
  const [draft, setDraft] = useState("");

  const send = () => {
    const content = draft.trim();
    if (!content || !canSend) return;
    // False only if the socket dropped in between — keep the draft then.
    if (sendMessage({ roomId, content })) setDraft("");
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send();
    }
  };

  const all: ComposerNotice[] = notice ? [{ text: notice, tone: "warn" }, ...notices] : notices;

  return (
    <div className={styles.composerWrap}>
      {all.map((n) => (
        <p
          key={n.text}
          className={`${styles.composerNotice} ${styles[n.tone]}`}
          role={n.tone === "error" ? "alert" : "status"}
        >
          <span>{n.text}</span>
          {n.action ? (
            <button type="button" className={styles.linkButton} onClick={n.action.onClick}>
              {n.action.label}
            </button>
          ) : null}
          {n.onDismiss ? (
            <button
              type="button"
              className={styles.dismissButton}
              onClick={n.onDismiss}
              aria-label="Dismiss"
            >
              ×
            </button>
          ) : null}
        </p>
      ))}
      <div className={styles.composer}>
        {leading}
        <label htmlFor={inputId} className={styles.srOnly}>
          Message
        </label>
        <input
          id={inputId}
          className={styles.input}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          maxLength={MAX_MESSAGE_CHARS}
          disabled={!canSend}
          autoComplete="off"
        />
        <button
          type="button"
          className={styles.sendButton}
          onClick={send}
          disabled={!canSend || !draft.trim()}
          aria-label="Send"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="m22 2-7 20-4-9-9-4z" />
            <path d="M22 2 11 13" />
          </svg>
        </button>
      </div>
    </div>
  );
}
